import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ActiveSessionCard = ({ session, onIntensityAdjust, onSessionMonitor }) => {
  const navigate = useNavigate();
  const [timeElapsed, setTimeElapsed] = useState(session?.duration || 0);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeElapsed(prev => prev + 1);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins?.toString()?.padStart(2, '0')}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-success text-success-foreground';
      case 'rest': return 'bg-warning text-warning-foreground';
      case 'paused': return 'bg-muted text-muted-foreground';
      case 'completed': return 'bg-secondary text-secondary-foreground';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return 'Play';
      case 'rest': return 'Clock';
      case 'paused': return 'Pause';
      case 'completed': return 'CheckCircle';
      default: return 'Circle';
    }
  };

  const handleMonitorSession = () => {
    navigate(`/exercise-execution?session=${session?.id}&monitor=true`);
    if (onSessionMonitor) {
      onSessionMonitor(session?.id);
    }
  };

  const handleIntensityAdjust = () => {
    if (onIntensityAdjust) {
      onIntensityAdjust(session?.id, session?.currentIntensity);
    }
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-6 shadow-md hover:shadow-lg transition-all duration-200">
      {/* Client Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
            <Icon name="User" size={24} className="text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground text-lg">{session?.clientName}</h3>
            <p className="text-sm text-muted-foreground">ID: {session?.clientId}</p>
          </div>
        </div>
        
        <div className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(session?.status)}`}>
          <div className="flex items-center space-x-1">
            <Icon name={getStatusIcon(session?.status)} size={12} />
            <span className="capitalize">{session?.status}</span>
          </div>
        </div>
      </div>
      {/* Current Exercise */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium text-muted-foreground">Current Exercise</p>
          <span className="text-sm font-mono text-foreground">{formatTime(timeElapsed)}</span>
        </div>
        <h4 className="font-semibold text-foreground text-base mb-1">{session?.currentExercise}</h4>
        <div className="flex items-center space-x-4 text-sm text-muted-foreground">
          <span>Set {session?.currentSet} of {session?.totalSets}</span>
          <span>•</span>
          <span>Intensity: {session?.currentIntensity}</span>
          <span>•</span>
          <span>{session?.targetReps} reps target</span>
        </div>
      </div>
      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-muted-foreground">Session Progress</span>
          <span className="text-xs text-muted-foreground">
            {session?.completedExercises} of {session?.totalExercises} exercises
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{ width: `${(session?.completedExercises / session?.totalExercises) * 100}%` }}
          />
        </div>
      </div>
      {/* Last Completed Set */}
      {session?.lastCompletedReps && (
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Last Set</span>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-foreground">
                {session?.lastCompletedReps} reps
              </span>
              {session?.lastCompletedReps >= session?.targetReps ? (
                <Icon name="TrendingUp" size={16} className="text-success" />
              ) : (
                <Icon name="TrendingDown" size={16} className="text-warning" />
              )}
            </div>
          </div>
        </div>
      )}
      {/* Action Buttons */}
      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleMonitorSession}
          className="flex-1 breathing-focus"
        >
          <Icon name="Eye" size={16} className="mr-2" />
          Monitor
        </Button>
        
        <Button
          variant="secondary"
          size="sm"
          onClick={handleIntensityAdjust}
          className="flex-1 breathing-focus"
          disabled={session?.status === 'completed'}
        >
          <Icon name="Settings" size={16} className="mr-2" />
          Adjust
        </Button>
      </div>
      {/* Alert Indicators */}
      {session?.alerts && session?.alerts?.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm text-warning font-medium">
              {session?.alerts?.length} alert{session?.alerts?.length > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActiveSessionCard;